import { EventDate } from '../EventDate/EventDate.component';
import { EventInfos } from '../EventInfos/EventInfos.component';
import { EventLocation } from '../EventLocation/EventLocation.component';
import { EventContext } from 'pages/evenement/[slug]';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { ContentAuthor } from '@components/content/ContentAuthor/ContentAuthor.component';
import { ContentBody } from '@components/content/ContentBody/ContentBody.component';
import { ContentFooter } from '@components/content/ContentFooter/ContentFooter.component';
import { ContentLayout, ContentLayoutLoaderOrError } from '@components/content/ContentLayout/ContentLayout.component';
import ReadAlso from '@components/ReadAlso/ReadAlso.component';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { Constants } from '@utils/Constants';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

import style from './eventLayout.module.scss';
const EventLayout: FC<ComponentPropsWithRef<'div'>> = ({ className, ...rest }) => {
  const {
    event,
    readAlso,
    isEventValidating,
  } = useContext(EventContext);
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  useAdvert({
    pageId: ADVERT_PAGE_ID.Event,
  });
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const adClassName = useClassNames({ all: 'plc-mt-m', m: ['plc-mt-0', 'plc-ml-m'] });

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: isEventValidating.initialData,
      level2: ATLevel2.Events,
      tagPageNameI18nArray: [
        'events.tag.title',
        event.initialData ? { code: event.initialData.slug, notI18n: true } : undefined,
      ],
    }),
    [isEventValidating.initialData, event.initialData]
  );

  const rootClassName = classnames('event', className);
  if (!event.initialData) {
    return (
      <ContentLayoutLoaderOrError
        className={rootClassName}
        isLoading={isEventValidating.initialData}
        //retryCallback={retryCallback}
        withIllu={false}
        {...rest}
      />
    );
  }

  const {
    author,
    body,
    interestCodes = Constants.EMPTY_ARRAY,
    activityCodes = Constants.EMPTY_ARRAY,
    address,
    metaTitle,
    metaDescription,
    title,
    organizationEmail,
    organizationName,
    organizationPhone,
    organizationWebsite,
    startDate,
    endDate,
    canonicalLink,
    slug,
  } = event.initialData;

  const steps: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.home, href: urlEnum.home },
    },
    {
      label: title,
      notI18n: true,
    },
  ];

  return (
    <ContentLayout
      articleTitle={title}
      articleUrl={`${urlEnum.event}/${slug}`}
      breadcrumbSteps={steps}
      breakpoint={Breakpoint.l}
      className={rootClassName}
      gridInfosComponent={
        <>
          <EventInfos
            address={address}
            className="plc-mb-basis"
            email={organizationEmail}
            name={organizationName}
            phone={organizationPhone}
            website={organizationWebsite}
          />
          <GenericAd formatId={isSmartphone ? ADVERT_FORMAT_ID.MegaBanner : ADVERT_FORMAT_ID.WideAngle} />
          <div className={isSmartphone ? '' : `${style['grid-pub mobile-pub-center']} plc-mb-basis plc-pt-basis`}>
            <GenericAd className={adClassName} formatId={ADVERT_FORMAT_ID.WideAngle} />
          </div>
        </>
      }
      metas={{
        canonical: canonicalLink,
        metaDescI18n: metaDescription,
        metaTitleI18n: metaTitle || title,
      }}
      postTitleComponent={
        <>
          <ContentAuthor author={author} />
          <EventLocation address={address} className="plc-mt-s plc-mt-up-m-0" />
        </>
      }
      preTitleComponent={<EventDate compact={isMobile} endDate={new Date(endDate)} startDate={new Date(startDate)} />}
      tagProps={tagProps}
      {...rest}
    >
      <ContentBody body={body} isPartial={false} />
      <ContentFooter 
        activityCodes={activityCodes}
        className="plc-mb-l plc-mb-up-m-xl"
        interestCodes={interestCodes}
        />
      <ReadAlso {...readAlso} />
    </ContentLayout>
  );
};

export { EventLayout };
